<script setup lang="ts">
import { computed, ref } from 'vue';
import { DailyRecord } from '@/types/sector';
import { sortByDateDesc } from '@/utils/score';
import { useDeleteRecord } from '@/composables/useSectorData';

const props = defineProps<{ records: DailyRecord[] }>();
const emit = defineEmits<{ edit: [record: DailyRecord] }>();

const PAGE_SIZE = 15;
const currentPage = ref(1);
const deleting = ref<string | null>(null);
const deleteMutation = useDeleteRecord();

const sorted = computed(() => sortByDateDesc(props.records));
const totalPages = computed(() => Math.max(1, Math.ceil(sorted.value.length / PAGE_SIZE)));
const paged = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return sorted.value.slice(start, start + PAGE_SIZE);
});

const pageNumbers = computed(() => {
  const total = totalPages.value;
  const cur = currentPage.value;
  const pages: (number | '...')[] = [];
  const window = 2;
  for (let p = 1; p <= total; p++) {
    if (p === 1 || p === total || (p >= cur - window && p <= cur + window)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
});

async function handleDelete(date: string) {
  if (!confirm(`确定删除 ${date} 的数据吗？`)) return;
  deleting.value = date;
  try {
    await deleteMutation.mutateAsync(date);
  } catch (e) {
    alert('删除失败：' + (e as Error).message);
  } finally {
    deleting.value = null;
  }
}
</script>

<template>
  <section class="card">
    <div class="mb-4 flex items-center justify-between">
      <h2 class="flex items-center gap-2 text-base font-semibold">
        <span class="h-1.5 w-1.5 rounded-full" style="background: rgb(var(--color-primary));"></span>
        历史记录
        <span class="badge ml-1">{{ records.length }} 条</span>
      </h2>
    </div>

    <div v-if="records.length === 0" class="py-12 text-center text-ink-subtle">
      还没有任何记录
    </div>

    <div v-else class="overflow-x-auto">
      <!-- 桌面端表格 -->
      <table class="hidden w-full text-sm md:table">
        <thead>
          <tr class="border-b" style="border-color: rgb(var(--color-border));">
            <th class="px-3 py-2.5 text-left font-semibold text-ink-muted">日期</th>
            <th class="px-3 py-2.5 text-left font-semibold text-ink-muted">🥇 第一名</th>
            <th class="px-3 py-2.5 text-left font-semibold text-ink-muted">🥈 第二名</th>
            <th class="px-3 py-2.5 text-left font-semibold text-ink-muted">🥉 第三名</th>
            <th class="px-3 py-2.5 text-right font-semibold text-ink-muted">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in paged"
            :key="row.date"
            class="border-b transition-colors hover:bg-surface-muted"
            style="border-color: rgb(var(--color-border));"
          >
            <td class="px-3 py-2.5 font-mono text-ink-muted">{{ row.date }}</td>
            <td class="px-3 py-2.5"><span class="font-medium">{{ row.first }}</span></td>
            <td class="px-3 py-2.5">{{ row.second }}</td>
            <td class="px-3 py-2.5">{{ row.third }}</td>
            <td class="px-3 py-2.5 text-right">
              <button class="btn btn-ghost mr-1 px-2 py-1 text-xs" @click="emit('edit', row)">编辑</button>
              <button
                class="btn btn-danger px-2 py-1 text-xs"
                :disabled="deleting === row.date"
                @click="handleDelete(row.date)"
              >
                {{ deleting === row.date ? '...' : '删除' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 移动端卡片列表 -->
      <ul class="space-y-2 md:hidden">
        <li
          v-for="row in paged"
          :key="row.date"
          class="rounded-lg border p-3"
          style="border-color: rgb(var(--color-border)); background: rgb(var(--color-surface-subtle));"
        >
          <div class="mb-1 flex items-center justify-between">
            <span class="font-mono text-xs text-ink-muted">{{ row.date }}</span>
            <div class="flex gap-1">
              <button class="btn btn-ghost px-2 py-1 text-xs" @click="emit('edit', row)">编辑</button>
              <button class="btn btn-danger px-2 py-1 text-xs" @click="handleDelete(row.date)">删除</button>
            </div>
          </div>
          <div class="text-sm">🥇 <span class="font-medium">{{ row.first }}</span></div>
          <div class="text-sm text-ink-muted">🥈 {{ row.second }}</div>
          <div class="text-sm text-ink-muted">🥉 {{ row.third }}</div>
        </li>
      </ul>

      <!-- 分页 -->
      <nav v-if="totalPages > 1" class="mt-4 flex flex-wrap items-center justify-center gap-1.5">
        <button class="btn btn-ghost px-2.5 py-1 text-xs" :disabled="currentPage === 1" @click="currentPage = 1">« 首页</button>
        <button class="btn btn-ghost px-2.5 py-1 text-xs" :disabled="currentPage === 1" @click="currentPage--">上一页</button>
        <template v-for="(p, i) in pageNumbers" :key="i">
          <span v-if="p === '...'" class="px-2 text-ink-subtle">…</span>
          <button
            v-else
            class="btn px-2.5 py-1 text-xs"
            :class="p === currentPage ? 'btn-primary' : 'btn-ghost'"
            @click="currentPage = p"
          >
            {{ p }}
          </button>
        </template>
        <button class="btn btn-ghost px-2.5 py-1 text-xs" :disabled="currentPage === totalPages" @click="currentPage++">下一页</button>
        <button class="btn btn-ghost px-2.5 py-1 text-xs" :disabled="currentPage === totalPages" @click="currentPage = totalPages">末页 »</button>
      </nav>
    </div>
  </section>
</template>
